import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Pack carousel controller for sections and product-detail blocks.
 *
 * Section mode re-renders via the Section Rendering API when the selected pack
 * variant changes. Block mode morphs the adjacent `.product-pack-card` from the
 * variant update HTML (same pattern as delivery-estimate / product-modules).
 *
 * @extends {Component}
 */
class PackCarousel extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.sectionId) {
      document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
      return;
    }

    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.dataset.sectionId) {
      document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
      return;
    }

    const section = this.closest('.shopify-section, dialog');
    section?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  /**
   * @param {Event} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (!detail) return;

    const newProduct = detail.data?.newProduct;

    if (newProduct?.id) {
      this.dataset.productId = String(newProduct.id);
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId &&
      this.dataset.productId &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const variantId = detail.resource?.id;
    if (!variantId) return;

    if (this.dataset.variantId && String(variantId) === String(this.dataset.variantId)) return;

    if (this.dataset.sectionId) {
      this.#renderSection(variantId, newProduct);
      return;
    }

    if (this.dataset.blockId) {
      this.#morphBlock(variantId, detail);
    }
  };

  /**
   * @param {string | number} variantId
   * @param {{ url?: string } | undefined} newProduct
   */
  #renderSection(variantId, newProduct) {
    const baseUrl = newProduct?.url ?? window.location.href;
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('variant', String(variantId));

    sectionRenderer
      .renderSection(this.dataset.sectionId, { url })
      .catch((error) => console.error('Pack carousel variant update failed:', error));
  }

  /**
   * @param {string | number} variantId
   * @param {any} detail
   */
  #morphBlock(variantId, detail) {
    const blockId = this.dataset.blockId;
    const html = detail.data?.html;
    if (!blockId || !(html instanceof Document || html instanceof Element)) return;

    const newController = html.querySelector(`pack-carousel[data-block-id="${blockId}"]`);
    const wrapper = this.#getPackCardWrapper();

    if (!newController) {
      wrapper?.remove();
      delete this.dataset.variantId;
      return;
    }

    const newWrapper = newController.nextElementSibling;
    if (!(newWrapper instanceof HTMLElement) || !newWrapper.classList.contains('product-pack-card')) {
      wrapper?.remove();
      delete this.dataset.variantId;
      return;
    }

    const importedWrapper = document.importNode(newWrapper, true);

    if (wrapper instanceof HTMLElement) {
      morph(wrapper, importedWrapper);
    } else {
      this.after(importedWrapper);
    }

    if (newController.dataset.variantId) {
      this.dataset.variantId = newController.dataset.variantId;
    } else {
      this.dataset.variantId = String(variantId);
    }

    if (newController.dataset.productId) {
      this.dataset.productId = newController.dataset.productId;
    }
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getPackCardWrapper() {
    const next = this.nextElementSibling;
    return next instanceof HTMLElement && next.classList.contains('product-pack-card') ? next : null;
  }
}

if (!customElements.get('pack-carousel')) {
  customElements.define('pack-carousel', PackCarousel);
}
